import { useEffect, useState } from 'react';
import { Plus, TriangleAlert, UserPlus } from 'lucide-react';
import {
  useGetMyChildrenQuery,
  useListDelegatesQuery,
  useAddEmergencyDelegateMutation,
  useCancelDelegateMutation,
  getApiError,
  type DelegateSummary,
} from '@/store/api';
import { PageLoader } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Mark } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input } from '@/components/ui/Field';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

const E164 = /^\+\d{10,15}$/;

const ERROR_NOTE =
  'flex items-start gap-2 rounded-md border border-destructive/25 bg-destructive/[0.06] ' +
  'px-2.5 py-2 text-[11px] font-medium text-[hsl(var(--record-ink-danger))]';

/*
  Authorising someone else to collect a child today.

  This is the screen a parent opens when they are stuck in traffic and an uncle
  has to go instead, so it is two fields and one button. Adding a delegate sends
  today's code to that person by SMS — the copy says so plainly, because a parent
  needs to know the other person will receive it without being told separately.
*/
export function ParentDelegatesPage() {
  const { data: children = [], isLoading: loadingChildren } = useGetMyChildrenQuery();
  const [childId, setChildId] = useState<string | null>(null);

  useEffect(() => {
    if (!childId && children.length > 0) setChildId(children[0].studentId);
  }, [children, childId]);

  const effectiveId = childId ?? children[0]?.studentId ?? '';

  const {
    data: delegates = [],
    isLoading: loadingDelegates,
    isError,
    error,
  } = useListDelegatesQuery(effectiveId, { skip: !effectiveId });

  const [addDelegate, addState] = useAddEmergencyDelegateMutation();
  const [cancelDelegate, cancelState] = useCancelDelegateMutation();

  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('+233');
  const [formError, setFormError] = useState('');
  const [pendingCancel, setPendingCancel] = useState<DelegateSummary | null>(null);

  const child = children.find((c) => c.studentId === effectiveId);
  const active = delegates.filter((d) => d.isActive);

  const closeAdd = () => {
    setShowAdd(false);
    setName('');
    setPhone('+233');
    setFormError('');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim().length < 2) {
      setFormError("Enter the person's full name.");
      return;
    }
    if (!E164.test(phone.trim())) {
      setFormError('Enter their number in international format, e.g. +233244123456');
      return;
    }
    setFormError('');
    try {
      await addDelegate({ studentId: effectiveId, name: name.trim(), phone: phone.trim() }).unwrap();
      closeAdd();
    } catch (err) {
      setFormError(getApiError(err));
    }
  };

  const doCancel = async () => {
    if (!pendingCancel) return;
    try {
      await cancelDelegate({ studentId: effectiveId, delegateId: pendingCancel.id }).unwrap();
      setPendingCancel(null);
    } catch {
      /* Kept open so the failure is visible. */
    }
  };

  if (loadingChildren) return <PageLoader label="Loading…" />;

  if (children.length === 0) {
    return (
      <div className={styles.panel}>
        <EmptyState
          icon={<UserPlus size={17} />}
          title="No children linked to this number"
          description="Ask the school office to add this phone number to your child's record."
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h1 className={styles.pageTitle}>Delegates</h1>
        <p className={cn(styles.meta, 'mt-0.5')}>
          Let someone else collect your child today. They get the code by SMS.
        </p>
      </div>

      {/* One child needs no picker; several do. */}
      {children.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto pb-0.5">
          {children.map((c) => (
            <button
              key={c.studentId}
              type="button"
              onClick={() => setChildId(c.studentId)}
              className={cn(
                'flex-shrink-0 rounded-md border px-2.5 py-1.5 text-[11.5px] font-semibold transition-colors',
                c.studentId === effectiveId
                  ? 'border-primary bg-accent text-primary'
                  : 'border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {c.studentName.split(' ')[0]}
            </button>
          ))}
        </div>
      )}

      <Button
        variant="primary"
        size="lg"
        className="w-full"
        icon={<Plus size={14} />}
        disabled={!effectiveId || child?.isAbsent}
        onClick={() => setShowAdd(true)}
      >
        Authorise someone for today
      </Button>

      {child?.isAbsent && (
        <p className="text-[11px] text-[hsl(var(--record-ink-warning))]">
          {child.studentName.split(' ')[0]} is marked absent today, so there is no code to share.
        </p>
      )}

      <div className={cn(styles.panel, 'overflow-clip')}>
        <div className="border-b border-border px-4 py-2.5">
          <h2 className={styles.sectionTitle}>
            Authorised now{child ? ` · ${child.studentName.split(' ')[0]}` : ''}
          </h2>
        </div>

        {loadingDelegates ? (
          <PageLoader label="Loading delegates…" />
        ) : isError ? (
          <EmptyState
            icon={<TriangleAlert size={17} />}
            title="Could not load delegates"
            description={getApiError(error)}
          />
        ) : active.length === 0 ? (
          <EmptyState
            icon={<UserPlus size={17} />}
            title="Nobody else is authorised"
            description="Only the collectors the school has on file can pick up right now."
          />
        ) : (
          active.map((d) => (
            <div
              key={d.id}
              className="flex items-center justify-between gap-2 border-b border-border px-4 py-3 last:border-b-0"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[12.5px] font-bold text-foreground">{d.name}</span>
                  <Mark tone={d.isRecurring ? 'info' : 'warning'}>
                    {d.isRecurring ? 'Recurring' : 'Today only'}
                  </Mark>
                </div>
                <p className="mt-0.5 font-mono text-[10.5px] text-muted-foreground">{d.phone}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="flex-shrink-0 text-[hsl(var(--record-ink-danger))] hover:bg-destructive/10"
                onClick={() => setPendingCancel(d)}
              >
                Remove
              </Button>
            </div>
          ))
        )}
      </div>

      <Modal
        open={showAdd}
        onClose={closeAdd}
        title="Authorise someone for today"
        description={`They will be sent ${child?.studentName.split(' ')[0] ?? 'your child'}'s pickup code by SMS, and it works today only.`}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={closeAdd}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form="add-delegate" loading={addState.isLoading}>
              Authorise &amp; send code
            </Button>
          </>
        }
      >
        <form id="add-delegate" onSubmit={submit} className="flex flex-col gap-3" noValidate>
          <Field label="Their full name" htmlFor="del-name" required>
            <Input
              id="del-name"
              autoFocus
              placeholder="Kofi Boateng"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field
            label="Their phone number"
            htmlFor="del-phone"
            hint="The code is sent here, so check it carefully."
            required
          >
            <Input
              id="del-phone"
              type="tel"
              inputMode="tel"
              placeholder="+233244123456"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </Field>
          {formError && (
            <div role="alert" className={ERROR_NOTE}>
              <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
              {formError}
            </div>
          )}
        </form>
      </Modal>

      <ConfirmDialog
        open={pendingCancel !== null}
        onClose={() => setPendingCancel(null)}
        onConfirm={() => void doCancel()}
        title={`Remove ${pendingCancel?.name ?? 'this person'}?`}
        confirmLabel="Remove access"
        loading={cancelState.isLoading}
        body={
          <>
            They lose access to today's code immediately. The code itself keeps working for you and
            for the collectors the school has on file.
            {cancelState.error && (
              <p className="mt-2 font-medium text-[hsl(var(--record-ink-danger))]">
                {getApiError(cancelState.error)}
              </p>
            )}
          </>
        }
      />
    </div>
  );
}
